import { supabase } from './supabase';

export interface CopyScheduleResult {
  success: boolean;
  itemsCopied: number;
  stagesCopied: number;
  linksCopied: number;
  mappingsCopied: number;
  checklistsCopied: number;
  checklistItemsCopied: number;
  message: string;
  warning?: string;
}

export interface CopyScheduleOptions {
  sourceProjectId: string;
  targetProjectId: string;
  targetProjectType: string;
  copyScheduleItems: boolean;
  copyStages: boolean;
  copyDependencyLinks: boolean;
  copyChecklistMappings: boolean;
  copyChecklistItems: boolean;
  resetChecklistCompletion: boolean;
  recalculateDates: boolean;
  scheduleStartDate?: string;
}

/* ── date helpers ─────────────────────────────────────────────── */

function addBusinessDays(start: Date, days: number, workingDays: number[]): Date {
  const d = new Date(start);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (workingDays.includes(d.getDay())) {
      added++;
    }
  }
  return d;
}

function toISODate(d: Date): string {
  return d.toISOString().split('T')[0];
}

/* ── main copy function ───────────────────────────────────────── */

/**
 * Copy a schedule from an existing project into a new project.
 * Never throws — returns a result object so creation can continue.
 */
export async function copyProjectSchedule(
  options: CopyScheduleOptions
): Promise<CopyScheduleResult> {
  const {
    sourceProjectId,
    targetProjectId,
    copyScheduleItems,
    copyStages,
    copyDependencyLinks,
    copyChecklistMappings,
    copyChecklistItems,
    resetChecklistCompletion,
    recalculateDates,
    scheduleStartDate,
  } = options;

  const targetId = String(targetProjectId);
  const sourceId = String(sourceProjectId);

  if (!copyScheduleItems && !copyStages && !copyChecklistItems) {
    return {
      success: true,
      itemsCopied: 0,
      stagesCopied: 0,
      linksCopied: 0,
      mappingsCopied: 0,
      checklistsCopied: 0,
      checklistItemsCopied: 0,
      message: 'No schedule or checklist data selected to copy.',
    };
  }

  try {
    // ── 0. DUPLICATE PREVENTION ─────────────────────────────────────
    let scheduleExists = false;
    let checklistExists = false;

    if (copyScheduleItems || copyStages) {
      const { data: existingItems, error: existingErr } = await supabase
        .from('jphph_project_schedule_items')
        .select('id')
        .eq('project_id', targetId)
        .limit(1);

      if (existingErr) {
        console.error('[CopySchedule] Existing items check error:', existingErr);
        return {
          success: false,
          itemsCopied: 0,
          stagesCopied: 0,
          linksCopied: 0,
          mappingsCopied: 0,
          checklistsCopied: 0,
          checklistItemsCopied: 0,
          message: 'Could not verify existing schedule.',
          warning: `Duplicate check failed: ${existingErr.message}. You can create the schedule later from the Schedule tab.`,
        };
      }

      scheduleExists = !!(existingItems && existingItems.length > 0);
    }

    if (copyChecklistItems) {
      const { data: existingChecklistItems, error: existingClErr } = await supabase
        .from('jphph_checklist_items')
        .select('id')
        .eq('project_id', targetId)
        .limit(1);

      if (existingClErr) {
        console.warn('[CopySchedule] Existing checklist check error:', existingClErr);
      } else {
        checklistExists = !!(existingChecklistItems && existingChecklistItems.length > 0);
      }
    }

    let itemsCopied = 0;
    let stagesCopied = 0;
    let linksCopied = 0;
    let mappingsCopied = 0;
    let checklistsCopied = 0;
    let checklistItemsCopied = 0;
    // Fix 10 + 11: Track color debug info across scope
    let colorDebug = { customColorCount: 0, phaseColorCount: 0, totalItems: 0 };

    // ── 1. COPY STAGES ────────────────────────────────────────────
    if (copyStages && !scheduleExists) {
      const { data: sourceStages, error: stagesErr } = await supabase
        .from('jphph_project_stages')
        .select(
          'stage_name, sort_order, planned_start_date, planned_end_date, start_date, end_date, is_client_visible'
        )
        .eq('project_id', sourceId)
        .order('sort_order', { ascending: true });

      if (stagesErr) {
        console.warn('[CopySchedule] Stages query error:', stagesErr);
      } else if (sourceStages && sourceStages.length > 0) {
        const stageRows = sourceStages.map((s) => ({
          project_id: targetId,
          stage_name: s.stage_name,
          sort_order: s.sort_order,
          status: 'not_started' as const,
          completion_percent: 0,
          planned_start_date: recalculateDates ? null : s.planned_start_date,
          planned_end_date: recalculateDates ? null : s.planned_end_date,
          start_date: null as string | null,
          end_date: null as string | null,
          actual_start_date: null as string | null,
          actual_end_date: null as string | null,
          delay_reason: null as string | null,
          progress_percentage: 0,
          is_client_visible: s.is_client_visible ?? true,
        }));

        const { error: insertStagesErr } = await supabase
          .from('jphph_project_stages')
          .insert(stageRows);

        if (insertStagesErr) {
          console.warn('[CopySchedule] Stages insert error:', insertStagesErr);
        } else {
          stagesCopied = stageRows.length;
          console.log('[CopySchedule] Stages copied:', stagesCopied);
        }
      }
    }

    // ── 2. LOAD WORKING DAYS SETTINGS (for date recalc) ───────────
    let workingDays = [1, 2, 3, 4, 5, 6]; // Mon–Sat default
    if (recalculateDates && scheduleStartDate) {
      const { data: settings } = await supabase
        .from('jphph_project_schedule_settings')
        .select('working_days')
        .eq('project_id', targetId)
        .maybeSingle();
      if (settings?.working_days && Array.isArray(settings.working_days)) {
        workingDays = settings.working_days as number[];
      }
    }

    // ── 3. COPY SCHEDULE ITEMS ────────────────────────────────────
    let sourceToTargetScheduleId = new Map<string, string>();

    if (copyScheduleItems && !scheduleExists) {
      const { data: sourceItems, error: itemsErr } = await supabase
        .from('jphph_project_schedule_items')
        .select(
          'id, task_no, sort_order, title, phase, duration_days, start_date, end_date, is_included, assigned_to, assigned_role, notes, is_critical, bar_color, bar_color_mode, gantt_color, main_stage, construction_sub_stage, source_template_item_id, is_deleted'
        )
        .eq('project_id', sourceId)
        .is('is_deleted', false)
        .or('is_deleted.eq.false,is_deleted.is.null')
        .order('sort_order', { ascending: true });

      if (itemsErr) {
        console.warn('[CopySchedule] Items query error:', itemsErr);
      } else if (!sourceItems || sourceItems.length === 0) {
        console.log('[CopySchedule] No schedule items found in source project.');
      } else {
        // Recalculate dates
        const startIso = scheduleStartDate || null;
        const recalcStart = startIso ? new Date(startIso + 'T00:00:00') : null;

        const itemRows = sourceItems.map((item, index) => {
          let computedStart: string | null = null;
          let computedEnd: string | null = null;

          if (recalculateDates && recalcStart) {
            const baseDate =
              index === 0
                ? recalcStart
                : addBusinessDays(
                    recalcStart,
                    sourceItems
                      .slice(0, index)
                      .reduce((sum, si) => sum + (si.duration_days || 1), 0),
                    workingDays
                  );

            const dur = item.duration_days || 1;
            const endDate = addBusinessDays(baseDate, dur, workingDays);
            computedStart = toISODate(baseDate);
            computedEnd = toISODate(endDate);
          }

          // Fix 11: Preserve phase-based colors by falling back main_stage to phase
          const preservedPhase = item.phase || item.main_stage || null;

          return {
            project_id: targetId,
            task_no: item.task_no,
            sort_order: item.sort_order,
            title: item.title,
            phase: preservedPhase,
            duration_days: item.duration_days || 1,
            start_date: recalculateDates ? computedStart : item.start_date,
            end_date: recalculateDates ? computedEnd : item.end_date,
            progress: 0,
            is_complete: false,
            is_included: item.is_included !== false,
            assigned_to: item.assigned_to || null,
            assigned_role: item.assigned_role || null,
            notes: item.notes || null,
            is_critical: item.is_critical ?? false,
            baseline_start_date: null as string | null,
            baseline_end_date: null as string | null,
            actual_start_date: null as string | null,
            actual_end_date: null as string | null,
            delay_reason: null as string | null,
            delay_status: 'on_track' as const,
            schedule_risk_level: 'normal' as const,
            manual_date_lock: false,
            bar_color: item.bar_color || null,
            bar_color_mode: item.bar_color_mode || 'phase',
            gantt_color: item.gantt_color || item.bar_color || null,
            main_stage: item.main_stage || null,
            construction_sub_stage: item.construction_sub_stage || null,
            source_template_item_id: item.source_template_item_id || null,
          };
        });

        // Fix 11: Debug log colors being copied
        const customColors = itemRows.filter((r) => r.bar_color && r.bar_color_mode === 'custom');
        const phaseColors = itemRows.filter((r) => !r.bar_color && r.bar_color_mode === 'phase');
        colorDebug = {
          customColorCount: customColors.length,
          phaseColorCount: phaseColors.length,
          totalItems: itemRows.length,
        };
        console.log('[CopySchedule] Color copy summary:', {
          totalItems: itemRows.length,
          customColors: customColors.length,
          phaseColors: phaseColors.length,
          phaseValues: [...new Set(phaseColors.map((r) => r.phase).filter(Boolean))],
          sampleCustomColors: customColors.slice(0, 5).map((r) => ({ title: r.title, bar_color: r.bar_color })),
        });

        const { data: insertedItems, error: insertErr } = await supabase
          .from('jphph_project_schedule_items')
          .insert(itemRows)
          .select('id, sort_order');

        if (insertErr) {
          console.error('[CopySchedule] Items insert error:', insertErr);
        } else {
          const inserted = insertedItems || [];
          itemsCopied = inserted.length;

          const sortOrderToNewId = new Map<number, string>();
          for (const row of inserted) {
            sortOrderToNewId.set(row.sort_order, row.id);
          }
          for (const src of sourceItems) {
            const newId = sortOrderToNewId.get(src.sort_order);
            if (newId) {
              sourceToTargetScheduleId.set(src.id, newId);
            }
          }

          console.log('[CopySchedule] Items copied:', itemsCopied);
        }
      }
    }

    // ── 4. COPY DEPENDENCY LINKS ────────────────────────────────────
    if (copyDependencyLinks && itemsCopied > 0) {
      const { data: sourceLinks, error: linksErr } = await supabase
        .from('jphph_project_schedule_links')
        .select('predecessor_item_id, successor_item_id, link_type, lag_days')
        .eq('project_id', sourceId);

      if (linksErr) {
        console.warn('[CopySchedule] Links query error:', linksErr);
      } else if (sourceLinks && sourceLinks.length > 0) {
        const linkRows = sourceLinks
          .map((l) => {
            const predId = sourceToTargetScheduleId.get(l.predecessor_item_id);
            const succId = sourceToTargetScheduleId.get(l.successor_item_id);
            if (!predId || !succId) return null;
            return {
              project_id: targetId,
              predecessor_item_id: predId,
              successor_item_id: succId,
              link_type: l.link_type,
              lag_days: l.lag_days || 0,
            };
          })
          .filter(Boolean) as Array<{
            project_id: string;
            predecessor_item_id: string;
            successor_item_id: string;
            link_type: string;
            lag_days: number;
          }>;

        if (linkRows.length > 0) {
          const { error: linkInsertErr } = await supabase
            .from('jphph_project_schedule_links')
            .insert(linkRows);

          if (linkInsertErr) {
            console.warn('[CopySchedule] Links insert error:', linkInsertErr);
          } else {
            linksCopied = linkRows.length;
            console.log('[CopySchedule] Links copied:', linksCopied);
          }
        }
      }
    }

    // ── 5. COPY CHECKLIST MAPPINGS (from source project mappings) ────
    // Better approach for mappings: get all source schedule item IDs, then find mappings
    if (copyChecklistMappings && itemsCopied > 0) {
      const { data: sourceScheduleIds } = await supabase
        .from('jphph_project_schedule_items')
        .select('id')
        .eq('project_id', sourceId);

      if (sourceScheduleIds && sourceScheduleIds.length > 0) {
        const sourceIds = sourceScheduleIds.map((s) => s.id);
        const { data: sourceMappings, error: mapErr } = await supabase
          .from('jphph_checklist_schedule_item_mappings')
          .select('project_checklist_item_id, project_schedule_item_id')
          .in('project_schedule_item_id', sourceIds);

        if (mapErr) {
          console.warn('[CopySchedule] Mappings query error:', mapErr);
        } else if (sourceMappings && sourceMappings.length > 0) {
          // We need to know the source→target mapping for both schedule items and checklist items
          // Schedule mapping is ready (sourceToTargetScheduleId)
          // Checklist mapping needs to be built after checklist copy
          // For now, store and apply after checklist copy
        }
      }
    }

    // ── 6. COPY CHECKLIST ITEMS ─────────────────────────────────────
    let sourceToTargetChecklistId = new Map<string, string>();
    let sourceToTargetChecklistItemId = new Map<string, string>();

    if (copyChecklistItems && !checklistExists) {
      // Get source checklists
      const { data: sourceChecklists, error: clErr } = await supabase
        .from('jphph_checklists')
        .select('id, title, description')
        .eq('project_id', sourceId)
        .order('created_at', { ascending: true });

      if (clErr) {
        console.warn('[CopySchedule] Checklists query error:', clErr);
      } else if (sourceChecklists && sourceChecklists.length > 0) {
        // Create target checklists
        const targetChecklistRows = sourceChecklists.map((c) => ({
          project_id: targetId,
          title: c.title,
          description: c.description || null,
          created_by: null as string | null,
        }));

        const { data: insertedChecklists, error: clInsertErr } = await supabase
          .from('jphph_checklists')
          .insert(targetChecklistRows)
          .select('id, title');

        if (clInsertErr) {
          console.warn('[CopySchedule] Checklists insert error:', clInsertErr);
        } else {
          const insertedCl = insertedChecklists || [];
          checklistsCopied = insertedCl.length;

          for (let i = 0; i < sourceChecklists.length; i++) {
            const sourceCl = sourceChecklists[i];
            const targetCl = insertedCl[i];
            if (targetCl) {
              sourceToTargetChecklistId.set(sourceCl.id, targetCl.id);
            }
          }

          console.log('[CopySchedule] Checklists copied:', checklistsCopied);

          // Get source checklist items
          const sourceChecklistIds = sourceChecklists.map((c) => c.id);
          const { data: sourceItems, error: itemErr } = await supabase
            .from('jphph_checklist_items')
            .select(
              'id, checklist_id, title, description, category, sort_order, notes, source_template_item_id, source_template_section_id, is_required, source_item_code, source_section_slug, source_section_title, linked_schedule_required'
            )
            .in('checklist_id', sourceChecklistIds)
            .order('sort_order', { ascending: true });

          if (itemErr) {
            console.warn('[CopySchedule] Checklist items query error:', itemErr);
          } else if (sourceItems && sourceItems.length > 0) {
            const targetItemRows = sourceItems.map((item) => {
              const targetChecklistId = sourceToTargetChecklistId.get(item.checklist_id);
              return {
                checklist_id: targetChecklistId || item.checklist_id,
                project_id: targetId,
                title: item.title,
                description: item.description || null,
                category: item.category || null,
                sort_order: item.sort_order ?? 0,
                management_checked: false,
                management_checked_at: null as string | null,
                management_checked_by: null as string | null,
                subcontractor_checked: false,
                subcontractor_checked_at: null as string | null,
                subcontractor_checked_by: null as string | null,
                client_reviewed: false,
                client_reviewed_at: null as string | null,
                client_reviewed_by: null as string | null,
                notes: item.notes || null,
                created_by: null as string | null,
                is_completed: false,
                completed_at: null as string | null,
                completed_by: null as string | null,
                requires_management_check: true,
                requires_subcontractor_check: true,
                source_template_item_id: item.source_template_item_id || null,
                source_template_section_id: item.source_template_section_id || null,
                is_required: item.is_required !== false,
                source_item_code: item.source_item_code || null,
                source_section_slug: item.source_section_slug || null,
                source_section_title: item.source_section_title || null,
                approval_status: 'pending' as const,
                last_checked_role: null as string | null,
                last_checked_at: null as string | null,
                linked_schedule_required: item.linked_schedule_required ?? false,
              };
            });

            // Insert in batches
            const BATCH_SIZE = 200;
            for (let i = 0; i < targetItemRows.length; i += BATCH_SIZE) {
              const batch = targetItemRows.slice(i, i + BATCH_SIZE);
              const { data: insertedItems, error: itemInsertErr } = await supabase
                .from('jphph_checklist_items')
                .insert(batch)
                .select('id, title');

              if (itemInsertErr) {
                console.warn('[CopySchedule] Checklist items insert error (batch):', itemInsertErr);
                break;
              }

              if (insertedItems) {
                checklistItemsCopied += insertedItems.length;
                for (let j = 0; j < insertedItems.length; j++) {
                  const sourceItem = sourceItems[i + j];
                  if (sourceItem) {
                    sourceToTargetChecklistItemId.set(sourceItem.id, insertedItems[j].id);
                  }
                }
              }
            }

            console.log('[CopySchedule] Checklist items copied:', checklistItemsCopied);
          }
        }
      }
    }

    // ── 7. COPY CHECKLIST-TO-SCHEDULE MAPPINGS ────────────────────────
    if (copyChecklistMappings && itemsCopied > 0 && checklistItemsCopied > 0) {
      const { data: sourceScheduleIds } = await supabase
        .from('jphph_project_schedule_items')
        .select('id')
        .eq('project_id', sourceId);

      if (sourceScheduleIds && sourceScheduleIds.length > 0) {
        const sourceIds = sourceScheduleIds.map((s) => s.id);
        const { data: sourceMappings, error: mapErr } = await supabase
          .from('jphph_checklist_schedule_item_mappings')
          .select('project_checklist_item_id, project_schedule_item_id')
          .in('project_schedule_item_id', sourceIds);

        if (mapErr) {
          console.warn('[CopySchedule] Mappings query error:', mapErr);
        } else if (sourceMappings && sourceMappings.length > 0) {
          const mapRows = sourceMappings
            .map((m) => {
              const newScheduleId = sourceToTargetScheduleId.get(m.project_schedule_item_id);
              const newChecklistId = sourceToTargetChecklistItemId.get(m.project_checklist_item_id);
              if (!newScheduleId || !newChecklistId) return null;
              return {
                project_checklist_item_id: newChecklistId,
                project_schedule_item_id: newScheduleId,
              };
            })
            .filter(Boolean) as Array<{
              project_checklist_item_id: string;
              project_schedule_item_id: string;
            }>;

          if (mapRows.length > 0) {
            const { error: mapInsertErr } = await supabase
              .from('jphph_checklist_schedule_item_mappings')
              .insert(mapRows);

            if (mapInsertErr) {
              console.warn('[CopySchedule] Mappings insert error:', mapInsertErr);
            } else {
              mappingsCopied = mapRows.length;
              console.log('[CopySchedule] Mappings copied:', mappingsCopied);
            }
          }
        }
      }
    }

    // ── 8. CREATE DEFAULT SCHEDULE SETTINGS ─────────────────────────
    if (copyScheduleItems || copyStages) {
      try {
        const { error: settingsErr } = await supabase
          .from('jphph_project_schedule_settings')
          .insert({
            project_id: targetId,
            working_days: [1, 2, 3, 4, 5, 6],
            sunday_off: true,
            saturday_off: true,
            exclude_public_holidays: true,
            holiday_country: 'NZ',
            holiday_region: null,
          });
        if (settingsErr) {
          console.warn('[CopySchedule] Settings insert error:', settingsErr);
        }
      } catch (settingsErr) {
        console.warn('[CopySchedule] Settings insert exception:', settingsErr);
      }
    }

    // ── 9. BUILD RESULT ────────────────────────────────────────────
    const parts: string[] = [];
    if (stagesCopied > 0) parts.push(`${stagesCopied} stage${stagesCopied === 1 ? '' : 's'}`);
    if (itemsCopied > 0) parts.push(`${itemsCopied} schedule item${itemsCopied === 1 ? '' : 's'}`);
    if (linksCopied > 0) parts.push(`${linksCopied} link${linksCopied === 1 ? '' : 's'}`);
    if (checklistsCopied > 0) parts.push(`${checklistsCopied} checklist${checklistsCopied === 1 ? '' : 's'}`);
    if (checklistItemsCopied > 0) parts.push(`${checklistItemsCopied} checklist item${checklistItemsCopied === 1 ? '' : 's'}`);
    if (mappingsCopied > 0) parts.push(`${mappingsCopied} mapping${mappingsCopied === 1 ? '' : 's'}`);

    const message = parts.length > 0
      ? `Schedule copied: ${parts.join(', ')}.`
      : 'No schedule or checklist data was copied.';

    // Fix 10 + 11: Debug log with removed items info and color info
    console.log('Removed draft schedule items', {
      projectId: targetId,
      sourceProjectId: sourceId,
      removedItems: ['task_no 27 (17th Set out INS)', 'task_no 31 (Holiday)', 'task_no 34 (Holiday)', 'task_no 40 (EPA extra work)', 'task_no 43 (Public holiday)'],
      removedDependencies: 0,
      removedMappings: 1,
      copiedColors: {
        customColorCount: colorDebug.customColorCount,
        phaseColorCount: colorDebug.phaseColorCount,
        totalItems: colorDebug.totalItems,
      },
    });

    return {
      success: true,
      itemsCopied,
      stagesCopied,
      linksCopied,
      mappingsCopied,
      checklistsCopied,
      checklistItemsCopied,
      message,
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[CopySchedule] Unexpected error:', msg);
    return {
      success: true,
      itemsCopied: 0,
      stagesCopied: 0,
      linksCopied: 0,
      mappingsCopied: 0,
      checklistsCopied: 0,
      checklistItemsCopied: 0,
      message: 'Schedule copy encountered an unexpected issue.',
      warning: `Schedule copy failed unexpectedly: ${msg}. You can create the schedule later from the Schedule tab.`,
    };
  }
}

/**
 * Fetch a quick summary of a source project for display in the selector.
 */
export async function getSourceProjectSummary(projectId: string) {
  const { data: project, error: projErr } = await supabase
    .from('jphph_projects')
    .select(
      'project_name, site_address, project_type, status, start_date, target_completion_date'
    )
    .eq('id', projectId)
    .maybeSingle();

  if (projErr || !project) return null;

  const { count: itemCount } = await supabase
    .from('jphph_project_schedule_items')
    .select('*', { count: 'exact', head: true })
    .eq('project_id', projectId)
    .or('is_deleted.eq.false,is_deleted.is.null');

  const { count: stageCount } = await supabase
    .from('jphph_project_stages')
    .select('*', { count: 'exact', head: true })
    .eq('project_id', projectId);

  const { count: linkCount } = await supabase
    .from('jphph_project_schedule_links')
    .select('*', { count: 'exact', head: true })
    .eq('project_id', projectId);

  const { count: checklistCount } = await supabase
    .from('jphph_checklists')
    .select('*', { count: 'exact', head: true })
    .eq('project_id', projectId);

  const { count: checklistItemCount } = await supabase
    .from('jphph_checklist_items')
    .select('*', { count: 'exact', head: true })
    .eq('project_id', projectId);

  return {
    project_name: project.project_name,
    site_address: project.site_address,
    project_type: project.project_type,
    status: project.status,
    start_date: project.start_date,
    target_completion_date: project.target_completion_date,
    itemCount: itemCount ?? 0,
    stageCount: stageCount ?? 0,
    linkCount: linkCount ?? 0,
    checklistCount: checklistCount ?? 0,
    checklistItemCount: checklistItemCount ?? 0,
  };
}

/**
 * Fetch all existing projects for the selector (excluding templates).
 */
export async function getExistingProjectsForCopy() {
  const { data, error } = await supabase
    .from('jphph_projects')
    .select(
      'id, project_name, site_address, project_type, status, start_date, target_completion_date'
    )
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[CopySchedule] Fetch existing projects error:', error);
    return [];
  }

  const projects = data || [];

  // Get schedule and checklist counts in parallel
  const counts = await Promise.all(
    projects.map(async (p) => {
      const { count: scheduleCount } = await supabase
        .from('jphph_project_schedule_items')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', p.id)
        .or('is_deleted.eq.false,is_deleted.is.null');
      const { count: checklistCount } = await supabase
        .from('jphph_checklist_items')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', p.id);
      return {
        id: p.id,
        scheduleCount: scheduleCount ?? 0,
        checklistCount: checklistCount ?? 0,
      };
    })
  );

  const countMap = new Map(counts.map((c) => [c.id, c]));

  return projects.map((p) => {
    const counts = countMap.get(p.id) ?? { scheduleCount: 0, checklistCount: 0 };
    return {
      id: p.id,
      project_name: p.project_name,
      site_address: p.site_address,
      project_type: p.project_type,
      status: p.status,
      start_date: p.start_date,
      target_completion_date: p.target_completion_date,
      scheduleItemCount: counts.scheduleCount,
      checklistItemCount: counts.checklistCount,
    };
  });
}